/**
 * Conferma esplicita per il cambio di codice CER quando ci sono kg già a
 * saldo in area sotto il codice attuale (#116).
 *
 * Il backend risponde 409 elencando i conferimenti coinvolti: qui vengono
 * mostrati, e su conferma il chiamante rinvia la stessa PATCH con
 * `conferma_propagazione_ledger: true`. L'autorità resta il backend — questa
 * modale è solo il canale con cui l'utente esprime la conferma.
 *
 * Gemella di ConfermaModificaFiscaleModal: stesso impianto, stessa forma di
 * dialog. Per i due 409 NON confermabili c'è CerBloccatoModal, che non ha
 * pulsante di conferma.
 */
import { ArrowRight, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Modal from './ui/Modal';
import { type ConfermaPropagazioneDetail } from '../lib/cerLedger';
import { fmtKg } from '../lib/saldo';

interface Props {
  detail: ConfermaPropagazioneDetail;
  isSaving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfermaPropagazioneLedgerModal({ detail, isSaving, onConfirm, onCancel }: Props) {
  const { t } = useTranslation();

  return (
    <Modal
      title={t('richieste.propagazione_ledger.titolo')}
      subtitle={t('richieste.propagazione_ledger.sottotitolo')}
      onClose={onCancel}
      maxWidth="max-w-xl"
      testid="conferma-propagazione-ledger-modal"
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="px-3 py-1.5 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors disabled:opacity-60"
          >
            {t('common.azioni.annulla')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSaving}
            data-testid="conferma-propagazione-ledger-submit"
            className="px-3 py-1.5 text-xs font-medium text-white bg-accent-danger rounded-md hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {isSaving ? t('common.azioni.salvataggio_in_corso') : t('richieste.propagazione_ledger.conferma')}
          </button>
        </>
      }
    >
      <div className="flex gap-2.5 text-sm text-ink-dark">
        <AlertTriangle size={16} className="text-accent-warning flex-shrink-0 mt-0.5" />
        <p>{t('richieste.propagazione_ledger.avvertenza')}</p>
      </div>

      <div className="flex items-center gap-3 px-3 py-2.5 bg-stone-bg border border-stone-mid rounded-md">
        <span className="font-mono text-sm text-ink-dark">{detail.cer_attuale ?? '—'}</span>
        <ArrowRight size={14} className="text-ink-soft flex-shrink-0" />
        <span className="font-mono text-sm font-semibold text-ink-dark">{detail.cer_nuovo ?? '—'}</span>
        <span className="ml-auto text-xs text-ink-mid" data-testid="propagazione-totale">
          {t('richieste.propagazione_ledger.totale', { kg: fmtKg(detail.totale_kg) })}
        </span>
      </div>

      <div>
        <p className="text-xs text-ink-soft mb-1.5">
          {t('richieste.propagazione_ledger.conferimenti_coinvolti', { count: detail.conferimenti.length })}
        </p>
        <ul className="border border-stone-mid rounded-md divide-y divide-stone-mid max-h-52 overflow-y-auto">
          {detail.conferimenti.map((c) => (
            <li key={`${c.id}-${c.area_id}-${c.materiale}`} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="font-mono text-ink-dark flex-1">{c.numero ?? '—'}</span>
              <span className="text-xs text-ink-mid">
                {t('richieste.propagazione_ledger.area', { area: c.area_codice ?? '—' })}
              </span>
              <span className="text-ink-dark tabular-nums">{fmtKg(c.kg)} kg</span>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
